import { FileDownloadOutlined } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import FileSaver from "file-saver";
import { OpenApiFormat } from "model/schema/globalTypes";
import { useDataSourceOpenApiSpec } from "./hooks/useDataSources";

const useStyles = makeStyles(theme => ({
  actions: {
    display: "flex",
    gap: theme.spacing(2)
  },
  download: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: 12,
    lineHeight: "16px",
    // same grey as the edit icon in the section headers
    color: "#464646",
    "&:disabled": {
      cursor: "default",
      color: "#0000004D"
    },
    "&:focus-visible": {
      outline: "2px solid #0F789B"
    }
  },
  icon: {
    fontSize: "16px !important"
  }
}));

const fileTypes = {
  [OpenApiFormat.Json]: { extension: "json", mimeType: "application/json" },
  [OpenApiFormat.Yaml]: { extension: "yaml", mimeType: "application/yaml" }
};

export function useDataSourceSpecDownload(
  solutionKey: string,
  dataSourceKey: string,
  version: string
) {
  const [fetchSpec, { loading, error }] = useDataSourceOpenApiSpec();

  const download = async (format: OpenApiFormat) => {
    const { data } = await fetchSpec({
      variables: { solutionKey, dataSourceKey, version, format }
    });
    const spec =
      data?.solutions.solution?.cloudDataSources?.dataSource?.version
        ?.openApiSpec;
    if (!spec) return;
    const { extension, mimeType } = fileTypes[format];
    FileSaver.saveAs(
      new Blob([spec], { type: mimeType }),
      `${dataSourceKey}@${version}.openapi.${extension}`
    );
  };

  return { download, loading, error };
}

interface DataSourceSpecDownloadProps {
  onDownload: (format: OpenApiFormat) => void;
  disabled: boolean;
}

export default function DataSourceSpecDownload({
  onDownload,
  disabled
}: DataSourceSpecDownloadProps) {
  const classes = useStyles();
  return (
    <div className={classes.actions}>
      {[OpenApiFormat.Json, OpenApiFormat.Yaml].map(format => (
        <button
          key={format}
          type="button"
          className={classes.download}
          disabled={disabled}
          onClick={() => onDownload(format)}
          title={`Download the OpenAPI specification as ${fileTypes[
            format
          ].extension.toUpperCase()}`}
          data-testid={`data-source-spec-download-${fileTypes[format].extension}`}
        >
          <FileDownloadOutlined className={classes.icon} />
          {fileTypes[format].extension}
        </button>
      ))}
    </div>
  );
}
